<template>
  <div class="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
    <!-- Navigation Bar -->
    <nav class="relative z-10">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between items-center py-6">
          <!-- Logo -->
          <div class="flex items-center space-x-2">
            <div class="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
              <span class="text-white font-bold text-xl">P</span>
            </div>
            <span class="text-2xl font-bold text-gray-900">PopMates</span>
          </div>
          
          <!-- CTA Button for Desktop -->
          <div class="hidden sm:block">
            <BaseButton
              v-if="userStore.isAuthenticated"
              variant="primary"
              size="lg"
              @click="router.push('/app')"
              :loading="!userStore.initialized"
            >
              Go to App
            </BaseButton>
            <BaseButton
              v-else
              variant="primary"
              size="lg"
              @click="openLoginModal"
              :loading="!userStore.initialized"
            >
              Log In / Sign Up
            </BaseButton>
          </div>
        </div>
      </div>
    </nav>

    <!-- Hero Section -->
    <main class="relative">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-24">
        <div class="text-center">
          <!-- Main Heading -->
          <h1 class="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
            <span class="block">Discover what's</span>
            <span class="block bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              poppin' near you
            </span>
          </h1>
          
          <!-- Tagline -->
          <p class="text-xl sm:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto">
            See what's poppin' near you
          </p>
          
          <!-- Subtitle -->
          <p class="text-lg text-gray-500 mb-12 max-w-2xl mx-auto">
            Connect with friends, discover popular places, and see real-time activity around you. 
            Join the community and never miss out on what's happening nearby.
          </p>
          
          <!-- CTA Buttons -->
          <div class="flex flex-col sm:flex-row gap-4 justify-center items-stretch mb-16">
            <BaseButton
              v-if="userStore.isAuthenticated"
              variant="primary"
              size="xl"
              @click="router.push('/app')"
              :loading="!userStore.initialized"
              class="w-full sm:w-auto"
            >
              Go to App
            </BaseButton>
            <BaseButton
              v-else
              variant="primary"
              size="xl"
              @click="openLoginModal"
              :loading="!userStore.initialized"
              class="w-full sm:w-auto"
            >
              Get Started
            </BaseButton>
            
            <BaseButton
              variant="outline"
              size="xl"
              @click="scrollToFeatures"
              class="w-full sm:w-auto flex items-center justify-center"
            >
              Learn More
            </BaseButton>
            
            <!-- Dev-only Sentry test button -->
            <BaseButton
              v-if="isDev"
              variant="secondary"
              size="xl"
              @click="triggerSentryError"
              class="w-full sm:w-auto"
            >
              Trigger Sentry Error (dev)
            </BaseButton>
          </div>
          
          <!-- Hero Image/Illustration Placeholder -->
          <div class="relative mx-auto max-w-4xl">
            <div class="bg-white rounded-2xl shadow-2xl p-8 border border-gray-100">
              <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
                <!-- Feature Cards Preview -->
                <div class="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-6 text-center">
                  <div class="w-12 h-12 bg-blue-500 rounded-lg mx-auto mb-4 flex items-center justify-center">
                    <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                    </svg>
                  </div>
                  <h3 class="font-semibold text-gray-900 mb-2">Discover Places</h3>
                  <p class="text-sm text-gray-600">Find popular spots and hidden gems near you</p>
                </div>
                
                <div class="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-6 text-center">
                  <div class="w-12 h-12 bg-purple-500 rounded-lg mx-auto mb-4 flex items-center justify-center">
                    <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"/>
                    </svg>
                  </div>
                  <h3 class="font-semibold text-gray-900 mb-2">Connect with Friends</h3>
                  <p class="text-sm text-gray-600">See where your friends are hanging out</p>
                </div>
                
                <div class="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-6 text-center">
                  <div class="w-12 h-12 bg-green-500 rounded-lg mx-auto mb-4 flex items-center justify-center">
                    <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
                    </svg>
                  </div>
                  <h3 class="font-semibold text-gray-900 mb-2">Real-time Updates</h3>
                  <p class="text-sm text-gray-600">Live activity updates as they happen</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>

    <!-- Features Section -->
    <section ref="featuresSection" class="py-24 bg-white">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-16">
          <h2 class="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Why PopMates?
          </h2>
          <p class="text-xl text-gray-600 max-w-2xl mx-auto">
            More than just a map - it's your social compass for discovering what's happening around you.
          </p>
        </div>
        
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <!-- Feature 1 -->
          <div class="text-center">
            <div class="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg class="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
              </svg>
            </div>
            <h3 class="text-xl font-semibold text-gray-900 mb-3">Real-time Interest Tracking</h3>
            <p class="text-gray-600">See live activity at places near you and know when something's worth checking out.</p>
          </div>
          
          <!-- Feature 2 -->
          <div class="text-center">
            <div class="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg class="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
              </svg>
            </div>
            <h3 class="text-xl font-semibold text-gray-900 mb-3">Social Discovery</h3>
            <p class="text-gray-600">Connect with friends and discover places through your social network.</p>
          </div>
          
          <!-- Feature 3 -->
          <div class="text-center">
            <div class="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg class="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
              </svg>
            </div>
            <h3 class="text-xl font-semibold text-gray-900 mb-3">Privacy Focused</h3>
            <p class="text-gray-600">Control who can see your activity with flexible privacy settings.</p>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="py-24 bg-gradient-to-r from-blue-600 to-purple-600">
      <div class="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
        <h2 class="text-3xl sm:text-4xl font-bold text-white mb-6">
          Ready to see what's poppin'?
        </h2>
        <p class="text-xl text-blue-100 mb-8">
          Join thousands of users already discovering amazing places together.
        </p>
        <BaseButton
          variant="secondary"
          size="xl"
          @click="openLoginModal"
          :loading="!userStore.initialized"
          class="mx-auto"
        >
          Start Exploring Now
        </BaseButton>
      </div>
    </section>

    <!-- Login Modal -->
    <LoginModal 
      :show="showLoginModal" 
      @update:show="showLoginModal = $event"
      @close="showLoginModal = false"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/stores/userStore'
import { BaseButton, LoginModal } from '@/components/ui'
import * as Sentry from '@sentry/vue'

const router = useRouter()
const userStore = useUserStore()
const showLoginModal = ref(false)
const featuresSection = ref<HTMLElement>()
const isDev = computed(() => import.meta.env.DEV)

// Async auth redirect - shows UI immediately, redirects after auth initializes or login
// Checks loggingOut flag to prevent race condition with Supabase token auto-refresh
watch(
  () => [userStore.initialized, userStore.isAuthenticated, userStore.loggingOut] as const,
  ([isInitialized, isAuthenticated, isLoggingOut]) => {
    // Don't redirect if we just logged out (prevents race condition with token refresh)
    if (isLoggingOut) {
      console.log('🚫 Skipping redirect - logout in progress')
      userStore.loggingOut = false  // Clear the flag
      return
    }

    if (isInitialized && isAuthenticated) {
      console.log('🏠→📱 Auth initialized, user authenticated - redirecting to /app')
      router.replace('/app')
    }
  },
  { immediate: true }
)

const openLoginModal = () => {
  showLoginModal.value = true
}

const scrollToFeatures = () => {
  featuresSection.value?.scrollIntoView({ 
    behavior: 'smooth',
    block: 'start'
  })
}

const triggerSentryError = () => {
  try {
    // Intentional error to verify Sentry capture
    ;(window as any).myUndefinedFunction()
  } catch (err) {
    Sentry.captureException(err)
  }
}
</script>
